import {
    onMounted,
    ref,
    watchEffect,
} from "vue";

import { useLocale }                from "../locales/i18n.js";
import ImageFallback                from "../components/imageFallback.js";
import Store                        from "../store.js";
import PermissionsAuditCard         from "./settings/PermissionsAuditCard.js";


//////////////////////////////////////////////////
//  SETTINGS
//////////////////////////////////////////////////


export default {

    setup() {
        const { i18n }              = useLocale();
        const cssPrimaryColor       = window.getComputedStyle(document.body).getPropertyValue("--primary");
        const refColor              = ref(cssPrimaryColor);
        const refRoles                  = ref([]);
        const refMuteRoleId             = ref("");
        const refActivityWindowDays     = ref(7);
        const refChurnWindowDays        = ref(14);
        const refTargetActiveInteractions = ref(60);
        const refGuildConfigLoading = ref(false);
        const refGuildConfigSaving  = ref(false);
        const refGuildConfigSaved   = ref(false);

        onMounted(async () => {
            refGuildConfigLoading.value = true;

            try {
                const [ roles, config ] = await Promise.all([
                    fetch("/api/roles").then((res) => res.json()).catch(() => []),
                    fetch("/api/guild-config").then((res) => res.json()).catch(() => null),
                ]);

                refRoles.value = Array.isArray(roles)
                    ? roles.filter((role) => role.name !== "@everyone")
                    : [];
                refMuteRoleId.value = config?.moderation?.muteRoleId || "";
                refActivityWindowDays.value = Number(config?.analytics?.activityWindowDays || 7);
                refChurnWindowDays.value = Number(config?.analytics?.churnWindowDays || 14);
                refTargetActiveInteractions.value = Number(config?.analytics?.targetActiveInteractionsPerDay || 60);

            } finally {
                refGuildConfigLoading.value = false;
            }
        });

        function onInput(e) {
            refColor.value = e.target.value;
        }

        function onSwitchServer() {
            Store.me.guildMember    = null;
            Store.selectedGuild     = null;
        }

        async function onLogout() {
            await fetch("/api/logout", { method: "POST" });
            Store.me                = null;
            Store.selectedGuild     = null;
            location.reload();
        }

        async function saveGuildConfig() {
            refGuildConfigSaving.value = true;
            refGuildConfigSaved.value = false;

            try {
                const config = await fetch("/api/guild-config", {
                    method: "PATCH",
                    headers: {
                        "content-type": "application/json",
                    },
                body: JSON.stringify({
                    moderation: {
                        muteRoleId: refMuteRoleId.value || null,
                    },
                    analytics: {
                        activityWindowDays: Number(refActivityWindowDays.value),
                        churnWindowDays: Number(refChurnWindowDays.value),
                        targetActiveInteractionsPerDay: Number(refTargetActiveInteractions.value),
                    },
                }),
                }).then((res) => res.json());

                refMuteRoleId.value = config?.moderation?.muteRoleId || "";
                refActivityWindowDays.value = Number(config?.analytics?.activityWindowDays || 7);
                refChurnWindowDays.value = Number(config?.analytics?.churnWindowDays || 14);
                refTargetActiveInteractions.value = Number(config?.analytics?.targetActiveInteractionsPerDay || 60);
                refGuildConfigSaved.value = true;
                setTimeout(() => refGuildConfigSaved.value = false, 1800);

            } finally {
                refGuildConfigSaving.value = false;
            }
        }

        watchEffect(() => {
            localStorage["primaryColor"] = refColor.value;
            document.body.style.setProperty("--primary", refColor.value);
        });

        return () => (
            <div class="settings-page">
                <div class="settings-hero">
                    <span class="settings-eyebrow">Configuration</span>
                    <h1>Paramètres</h1>
                    <p>Gère le thème, le serveur actif et les options techniques de LogsCord.</p>
                </div>

                {Store.selectedGuild?.bot && <PermissionsAuditCard />}

                <div class="settings-grid">
                    <div class="settings-card settings-card-theme">
                        <div class="settings-card-icon">
                            <i class="material-symbols-outlined">palette</i>
                        </div>

                        <div class="settings-card-copy">
                            <strong>{i18n("page.settings.options.customize_theme_color.label")}</strong>
                            <span>Personnalise la couleur principale de l’interface.</span>
                        </div>

                        <input class="settings-color-input" type="color" value={refColor.value} onInput={onInput} />
                    </div>

                    <div class="settings-card settings-card-technical">
                        <div class="settings-card-icon">
                            <i class="material-symbols-outlined">terminal</i>
                        </div>

                        <div class="settings-card-copy">
                            <strong>{i18n("page.settings.options.api_key.label")}</strong>
                            <span>Gestion des accès techniques et intégrations.</span>
                        </div>

                        <button class="settings-button">
                            {i18n("actions.reset")}
                        </button>
                    </div>

                    <div class="settings-card settings-card-moderation">
                        <div class="settings-card-icon">
                            <i class="material-symbols-outlined">volume_off</i>
                        </div>

                        <div class="settings-card-copy">
                            <strong>Rôle muet</strong>
                            <span>Rôle utilisé pour détecter les mutes actifs dans les sanctions et profils.</span>
                        </div>

                        <div class="settings-inline-control">
                            <select
                                disabled={refGuildConfigLoading.value || refGuildConfigSaving.value}
                                value={refMuteRoleId.value}
                                onChange={(event) => refMuteRoleId.value = event.target.value}
                            >
                                <option value="">Aucun rôle défini</option>
                                {refRoles.value.map((role) => (
                                    <option value={role.id}>{role.name}</option>
                                ))}
                            </select>

                            <button class="settings-button" disabled={refGuildConfigLoading.value || refGuildConfigSaving.value} onClick={saveGuildConfig}>
                                {refGuildConfigSaving.value ? "Sauvegarde..." : refGuildConfigSaved.value ? "Sauvegardé" : "Enregistrer"}
                            </button>
                        </div>
                    </div>

                    <router-link class="settings-card settings-card-link" to="/settings/policies">
                        <div class="settings-card-icon">
                            <i class="material-symbols-outlined">policy</i>
                        </div>

                        <div class="settings-card-copy">
                            <strong>Politiques d'accès</strong>
                            <span>Gère les règles qui donnent accès aux ressources LogsCord par utilisateur ou rôle.</span>
                        </div>

                        <span class="settings-button settings-card-action">
                            Gérer
                        </span>
                    </router-link>

                    <div class="settings-card settings-card-analytics">
                        <div class="settings-card-icon">
                            <i class="material-symbols-outlined">insights</i>
                        </div>

                        <div class="settings-card-copy">
                            <strong>Coaching engagement</strong>
                            <span>Règles de calcul pour les recommandations d’amélioration de l’activité.</span>
                        </div>

                        <div class="settings-policy-builder settings-policy-builder--analytics">
                            <div class="settings-policy-field">
                                <label>Fenêtre activité (jours)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="30"
                                    value={refActivityWindowDays.value}
                                    onInput={(event) => refActivityWindowDays.value = Number(event.target.value)}
                                    disabled={refGuildConfigLoading.value || refGuildConfigSaving.value}
                                />
                            </div>

                            <div class="settings-policy-field">
                                <label>Fenêtre churn (jours)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="30"
                                    value={refChurnWindowDays.value}
                                    onInput={(event) => refChurnWindowDays.value = Number(event.target.value)}
                                    disabled={refGuildConfigLoading.value || refGuildConfigSaving.value}
                                />
                            </div>

                            <div class="settings-policy-field">
                                <label>Objectif interactions/jour</label>
                                <input
                                    type="number"
                                    min="5"
                                    max="5000"
                                    value={refTargetActiveInteractions.value}
                                    onInput={(event) => refTargetActiveInteractions.value = Number(event.target.value)}
                                    disabled={refGuildConfigLoading.value || refGuildConfigSaving.value}
                                />
                            </div>
                        </div>

                        <button class="settings-button" disabled={refGuildConfigLoading.value || refGuildConfigSaving.value} onClick={saveGuildConfig}>
                            {refGuildConfigSaving.value ? "Sauvegarde..." : refGuildConfigSaved.value ? "Sauvegardé" : "Enregistrer"}
                        </button>
                    </div>

                </div>

                <div class="settings-account-grid">
                    <button class="settings-account-card" onClick={onSwitchServer}>
                        <ImageFallback class="settings-server-switcher-icon" src={`https://cdn.discordapp.com/icons/${Store.selectedGuild.id}/${Store.selectedGuild.icon}`} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={Store.selectedGuild.name} />

                        <div class="settings-server-switcher-details">
                            <div class="settings-server-switcher-details-title">{Store.selectedGuild.name}</div>
                            <div class="settings-server-switcher-details-description">
                                <i class="material-symbols-outlined">refresh</i>
                                Changer de serveur
                            </div>
                        </div>
                    </button>

                    <button class="settings-account-card settings-account-card-danger" onClick={onLogout}>
                        <ImageFallback class="settings-server-switcher-icon" src={`https://cdn.discordapp.com/avatars/${Store.me.id}/${Store.me.avatar}`} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={Store.me.globalName || Store.me.username} />

                        <div class="settings-server-switcher-details">
                            <div class="settings-server-switcher-details-title">{Store.me.globalName || Store.me.username}</div>
                            <div class="settings-server-switcher-details-description">
                                <i class="material-symbols-outlined">logout</i>
                                Se déconnecter
                            </div>
                        </div>
                    </button>
                </div>

                {/* <div>
                    <div>
                        <div>
                            Séléctionner un type de logs
                        </div>


                    </div>

                    <div>
                        <div>
                            Séléctionner des salons à inclure ou exclure
                        </div>


                    </div>
                </div>

                {/* <div class="settings-masked-input">
                    {btoa(crypto.getRandomValues(new Uint8Array(32)))}
                </div> */}
            </div>
        );
    },
}
